// PROMPT 149: etiquetas de las conversaciones (las comparte todo el equipo).
import { useEffect, useRef, useState } from "react";
import { Plus, Tag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const COLORES_ETIQUETA = [
  { valor: "gris", nombre: "Gris", fondo: "#e2e8f0", texto: "#334155", punto: "#64748b" },
  { valor: "rojo", nombre: "Rojo", fondo: "#fee2e2", texto: "#b91c1c", punto: "#ef4444" },
  { valor: "naranja", nombre: "Naranja", fondo: "#ffedd5", texto: "#c2410c", punto: "#f97316" },
  { valor: "amarillo", nombre: "Amarillo", fondo: "#fef9c3", texto: "#a16207", punto: "#eab308" },
  { valor: "verde", nombre: "Verde", fondo: "#dcfce7", texto: "#15803d", punto: "#22c55e" },
  { valor: "azul", nombre: "Azul", fondo: "#dbeafe", texto: "#1d4ed8", punto: "#3b82f6" },
  { valor: "violeta", nombre: "Violeta", fondo: "#ede9fe", texto: "#6d28d9", punto: "#8b5cf6" },
  { valor: "rosa", nombre: "Rosa", fondo: "#fce7f3", texto: "#be185d", punto: "#ec4899" },
];
const POR_COLOR = Object.fromEntries(COLORES_ETIQUETA.map((c) => [c.valor, c]));

export function EtiquetaChip({ etiqueta, className = "" }) {
  const c = POR_COLOR[etiqueta && etiqueta.color] || POR_COLOR.gris;
  return (
    <span
      className={`inline-flex min-w-0 max-w-full shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${className}`}
      style={{ backgroundColor: c.fondo, color: c.texto }}
      title={etiqueta && etiqueta.nombre}
    >
      <span className="truncate">{etiqueta && etiqueta.nombre}</span>
    </span>
  );
}

function resolver(ids, etiquetas) {
  return (ids || []).map((id) => (etiquetas || []).find((e) => e.id === id)).filter(Boolean);
}

// En cada fila de la lista: hasta 2 etiquetas y "+n". Sin etiquetas no se ve nada.
export function EtiquetasEnFila({ ids, etiquetas, max = 2 }) {
  const lista = resolver(ids, etiquetas);
  if (!lista.length) return null;
  const resto = lista.length - max;
  return (
    <div className="mt-1 flex min-w-0 items-center gap-1" data-testid="wa-etiquetas-fila">
      {lista.slice(0, max).map((e) => (
        <EtiquetaChip key={e.id} etiqueta={e} />
      ))}
      {resto > 0 ? <span className="shrink-0 text-[11px] font-medium text-slate-500">+{resto}</span> : null}
    </div>
  );
}

export function SelectorColor({ valor, onElegir }) {
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Color">
      {COLORES_ETIQUETA.map((c) => (
        <button
          key={c.valor}
          type="button"
          role="radio"
          aria-checked={valor === c.valor}
          title={c.nombre}
          onClick={() => onElegir(c.valor)}
          className={`h-5 w-5 rounded-full border-2 ${valor === c.valor ? "border-slate-900" : "border-transparent"}`}
          style={{ backgroundColor: c.punto }}
        />
      ))}
    </div>
  );
}

// En el encabezado del chat: las etiquetas puestas y el boton para elegirlas o crear una.
export function EtiquetasDeLaConversacion({ conversacion, etiquetas, onCambio, onCrear }) {
  const [abierto, setAbierto] = useState(false);
  const [creando, setCreando] = useState(false);
  const [nombre, setNombre] = useState("");
  const [color, setColor] = useState("azul");
  const [ocupado, setOcupado] = useState(false);
  const caja = useRef(null);
  const convId = conversacion && conversacion.id;
  const puestas = (conversacion && conversacion.etiquetas) || [];
  const visibles = resolver(puestas, etiquetas);

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => {
      if (caja.current && !caja.current.contains(e.target)) setAbierto(false);
    };
    const tecla = (e) => {
      if (e.key === "Escape") setAbierto(false);
    };
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", tecla);
    };
  }, [abierto]);

  useEffect(() => {
    setAbierto(false);
    setCreando(false);
    setNombre("");
  }, [convId]);

  const guardar = async (ids) => {
    setOcupado(true);
    try {
      const { data } = await api.post(`/whatsapp/conversaciones/${encodeURIComponent(convId)}/etiquetas`, {
        etiquetas: ids,
      });
      onCambio(convId, (data && data.etiquetas) || ids);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setOcupado(false);
    }
  };

  const alternar = (id) => guardar(puestas.includes(id) ? puestas.filter((x) => x !== id) : [...puestas, id]);

  const crear = async () => {
    const limpio = nombre.trim();
    if (!limpio) return;
    setOcupado(true);
    let nueva = null;
    try {
      const { data } = await api.post("/whatsapp/etiquetas", { nombre: limpio, color });
      nueva = data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setOcupado(false);
    if (!nueva) return;
    if (onCrear) onCrear(nueva);
    setNombre("");
    setCreando(false);
    guardar([...puestas, nueva.id]);
  };

  return (
    <div className="relative flex min-w-0 flex-wrap items-center gap-1" ref={caja} data-testid="wa-etiquetas-conv">
      {visibles.slice(0, 3).map((e) => (
        <EtiquetaChip key={e.id} etiqueta={e} className="max-w-[8rem]" />
      ))}
      {visibles.length > 3 ? (
        <span className="text-[11px] font-medium text-slate-500">+{visibles.length - 3}</span>
      ) : null}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        data-testid="wa-etiquetas-boton"
        className="gap-1.5 bg-white"
      >
        <Tag className="h-3.5 w-3.5" /> Etiquetas
      </Button>
      {abierto ? (
        <div
          className="absolute right-0 top-full z-30 mt-1 w-64 rounded-lg border border-slate-200 bg-white p-2 shadow-lg"
          role="dialog"
          aria-label="Etiquetas de la conversación"
          data-testid="wa-etiquetas-panel"
        >
          {etiquetas.length === 0 ? (
            <p className="px-2 py-1.5 text-sm text-slate-500">Todavía no hay etiquetas.</p>
          ) : (
            <ul className="max-h-56 overflow-y-auto">
              {etiquetas.map((e) => (
                <li key={e.id}>
                  <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={puestas.includes(e.id)}
                      disabled={ocupado}
                      onChange={() => alternar(e.id)}
                      data-testid={`wa-etiqueta-check-${e.id}`}
                    />
                    <EtiquetaChip etiqueta={e} />
                  </label>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-1 border-t border-slate-100 pt-2">
            {creando ? (
              <div className="space-y-2 px-1">
                <Input
                  value={nombre}
                  maxLength={30}
                  autoFocus
                  onChange={(e) => setNombre(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") crear();
                  }}
                  placeholder="Nombre de la etiqueta"
                  className="h-8"
                  data-testid="wa-etiqueta-nombre"
                />
                <SelectorColor valor={color} onElegir={setColor} />
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setCreando(false)} disabled={ocupado}>
                    Cancelar
                  </Button>
                  <Button size="sm" onClick={crear} disabled={ocupado || !nombre.trim()} data-testid="wa-etiqueta-crear-ok">
                    Crear
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCreando(true)}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                data-testid="wa-etiqueta-crear"
              >
                <Plus className="h-4 w-4" /> Crear etiqueta
              </button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------ pantalla de gestion
function FilaEtiqueta({ etiqueta, ocupado, confirmando, onConfirmar, onCambiar, onBorrar }) {
  const [nombre, setNombre] = useState(etiqueta.nombre);

  useEffect(() => {
    setNombre(etiqueta.nombre);
  }, [etiqueta.nombre]);

  const guardarNombre = () => {
    const limpio = nombre.trim();
    if (limpio && limpio !== etiqueta.nombre) onCambiar(etiqueta, { nombre: limpio });
    else setNombre(etiqueta.nombre);
  };

  return (
    <li
      className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 p-2"
      data-testid={`wa-etiqueta-fila-${etiqueta.id}`}
    >
      <Input
        value={nombre}
        maxLength={30}
        disabled={ocupado}
        onChange={(e) => setNombre(e.target.value)}
        onBlur={guardarNombre}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        aria-label="Nombre de la etiqueta"
        className="h-8 min-w-0 flex-1 basis-32"
      />
      <SelectorColor valor={etiqueta.color} onElegir={(c) => onCambiar(etiqueta, { color: c })} />
      {confirmando ? (
        <span className="flex items-center gap-1 text-xs text-slate-600">
          ¿Borrarla? Se saca de todas las conversaciones.
          <Button variant="outline" size="sm" onClick={() => onBorrar(etiqueta)} className="text-red-600" data-testid="wa-etiqueta-borrar-si">
            Borrar
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onConfirmar(null)}>
            No
          </Button>
        </span>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onConfirmar(etiqueta.id)}
          aria-label="Borrar la etiqueta"
          className="h-8 w-8"
          data-testid={`wa-etiqueta-borrar-${etiqueta.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </li>
  );
}

export function EtiquetasDialog({ open, onOpenChange, etiquetas, onCambio }) {
  const [nombre, setNombre] = useState("");
  const [color, setColor] = useState("azul");
  const [ocupado, setOcupado] = useState(false);
  const [confirmando, setConfirmando] = useState(null);

  useEffect(() => {
    if (!open) {
      setConfirmando(null);
      setNombre("");
    }
  }, [open]);

  const llamar = async (accion, aviso) => {
    setOcupado(true);
    try {
      await accion();
      if (aviso) toast.success(aviso);
      if (onCambio) await onCambio();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setOcupado(false);
    }
  };

  const crear = () =>
    llamar(async () => {
      await api.post("/whatsapp/etiquetas", { nombre: nombre.trim(), color });
      setNombre("");
    }, "Etiqueta creada.");

  const cambiar = (e, cambios) =>
    llamar(() => api.put(`/whatsapp/etiquetas/${encodeURIComponent(e.id)}`, cambios));

  const borrar = (e) =>
    llamar(async () => {
      await api.delete(`/whatsapp/etiquetas/${encodeURIComponent(e.id)}`);
      setConfirmando(null);
    }, "Etiqueta borrada.");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="wa-etiquetas-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Etiquetas</DialogTitle>
          <DialogDescription>
            Las comparte todo el equipo. Se ponen desde el encabezado de cada chat y se filtran desde la lista. Hasta
            50.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 rounded-lg border border-slate-200 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={nombre}
              maxLength={30}
              onChange={(e) => setNombre(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && nombre.trim() && !ocupado) crear();
              }}
              placeholder="Nueva etiqueta"
              aria-label="Nombre de la nueva etiqueta"
              className="h-8 min-w-0 flex-1 basis-32"
              data-testid="wa-etiquetas-nueva-nombre"
            />
            <SelectorColor valor={color} onElegir={setColor} />
            <Button size="sm" onClick={crear} disabled={ocupado || !nombre.trim()} className="gap-1.5" data-testid="wa-etiquetas-nueva">
              <Plus className="h-4 w-4" /> Crear
            </Button>
          </div>
        </div>
        <ul className="space-y-2" data-testid="wa-etiquetas-lista">
          {etiquetas.length === 0 ? <li className="text-sm text-slate-500">Todavía no hay etiquetas.</li> : null}
          {etiquetas.map((e) => (
            <FilaEtiqueta
              key={e.id}
              etiqueta={e}
              ocupado={ocupado}
              confirmando={confirmando === e.id}
              onConfirmar={setConfirmando}
              onCambiar={cambiar}
              onBorrar={borrar}
            />
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
